/**
 * One organization's backups, on the host's Backups page: where archives go, when they are taken, the
 * archives themselves, the run log, and restoring one.
 *
 *   * THE HOST'S, NOT THE ORGANIZATION'S. Everything goes through `api` (`api/hostBackups.ts`) for the
 *     organization named in `options`; the organization's own administrators no longer see this.
 *   * IT NEVER HOLDS THE DESTINATION PASSWORD - empty box keeps it, a tick clears it.
 *   * THE ONE-LINE ANSWER TO "IS THIS ACTUALLY BACKING UP?" sits at the top, and a destination that
 *     cannot be listed is reported apart from a failed RUN - a share that has gone away says so even
 *     when the last run succeeded days ago.
 *   * RESTORE IS TYPED, NOT CLICKED. It replaces the organization's whole database, so the panel spells
 *     that out and wants RESTORE typed; the server re-checks it. The host can restart one organization,
 *     so a restore can be applied at once; a staged one drives a standing banner until it is.
 *   * SAVING RELOADS rather than binding the returned config alone: a new destination changes which
 *     archives are listed, and the table would otherwise still show the old folder.
 */
import { describeError } from '../../api/http.js';
import type { BackupApi } from '../../api/hostBackups.js';
import {
  BackupFrequency,
  BackupLimits,
  BackupTrigger,
  type BackupDto,
  type BackupRunDto,
  type BackupStatusDto,
  type DayOfWeek,
  type UpdateBackupConfigRequest
} from '../../contracts.gen.js';
import { download, pickFiles } from '../../download.js';
import { bytes, join, localDateTime } from '../../format.js';
import { CardList } from '../../ui/CardList.js';
import type { Card } from '../../ui/Card.js';
import { chip } from '../../ui/Chip.js';
import { confirm, tell } from '../../ui/dialogs.js';
import { StatusLine } from '../../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  checkLine,
  comboBox,
  field,
  fieldRow,
  fillWith,
  FONT,
  GAP,
  h2,
  muted,
  passwordBox,
  relayout,
  splitRow,
  stackOf,
  text,
  textBox,
  value,
  vstack,
  wrappingCheck
} from '../../ui/layout.js';
import type { PageContext } from '../page.js';

/** In `BackupFrequency` order. */
const FREQUENCIES = ['Only when asked', 'Hourly', 'Daily', 'Weekly'] as const;
/** In `DayOfWeek` order. */
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
const HOURS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`);

export interface BackupSection {
  card: Card;
  load(): Promise<void>;
}

export interface BackupSectionOptions {
  /** The organization's name, for the wording. */
  organization: string;
  /** Whether the operator may change, run, download and restore (an administrator in it). */
  canEdit: boolean;
  /** After anything that changes the summary the page shows above (a save, a run, a restore). */
  onChanged?: () => void;
}

/** An archive in the folder, or a file picked from this computer. */
type RestoreSource = { kind: 'archive'; backup: BackupDto } | { kind: 'file'; file: File };

function whole(text: string): number | null {
  const trimmed = text.trim();
  return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
}

export function backupSection(ctx: PageContext, api: BackupApi, options: BackupSectionOptions): BackupSection {
  const { organization, canEdit } = options;
  const changed = (): void => options.onChanged?.();
  let notificationsDeliverable = false;

  const status = new StatusLine();

  // ---- The staged-restore banner ----
  const stagedBanner = card({ tint: 'warn', visible: false });
  stagedBanner.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm }),
      text('A restore is staged and waiting for a restart.', { font: FONT.bold }),
      muted(
        `${organization}'s database is replaced the next time it starts. Restart it now to complete the restore - its people are signed out for the few seconds that takes - or cancel it.`
      ),
      stackOf(
        buttonRow(),
        button('Restart now to apply it', () => void applyStaged(), true),
        button('Cancel the staged restore', () => void cancelStaged())
      )
    )
  );

  // ---- Status lines ----
  const lastRunLine = text('');
  const nextRunLine = muted('');
  const destinationError = muted('', 'danger');
  const freeSpace = muted('');

  // ---- The form ----
  const pathBox = textBox({ placeholder: '/volume1/backups/jetsoncrm/organization  or  \\\\diskstation\\backups\\organization' });
  const userBox = textBox();
  const passwordInput = passwordBox();
  const passwordState = muted('');
  const clearPassword = wrappingCheck('Clear the stored destination password');
  const enabledCheck = wrappingCheck('Take backups automatically');
  const frequencyPicker = comboBox<string>(FREQUENCIES, { selectedIndex: BackupFrequency.Daily });
  const dayPicker = comboBox<string>(DAYS, { selectedIndex: 0 });
  const hourPicker = comboBox<string>(HOURS, { selectedIndex: 2 });
  const dayField = field('Day', dayPicker);
  const hourField = field("At (this server's local time)", hourPicker);
  const keepCountBox = textBox({ text: '14' });
  const keepDaysBox = textBox({ text: '0' });
  const notifyBox = textBox({ placeholder: 'you@yourcompany.com' });
  const undeliverable = muted(
    "The host's mail account (Billing → Accounts) cannot send, so these notifications cannot be delivered. Configure sending there, or clear this address so it does not read as covered.",
    'danger'
  );
  const includeMedia = wrappingCheck('Include knowledge-base, forum and installer files', true);
  const testVerdict = muted('');

  const saveButton = button('Save backup settings', () => void save(), true);
  const testButton = button('Test destination', () => void test());
  const runButton = button('Back up now', () => void run());
  const actionButtons = [saveButton, testButton, runButton];

  for (const c of [pathBox, userBox, passwordInput, clearPassword, enabledCheck, frequencyPicker, dayPicker, hourPicker, keepCountBox, keepDaysBox, notifyBox, includeMedia, ...actionButtons]) {
    c.enabled = canEdit;
  }

  function showSchedule(): void {
    const f = frequencyPicker.selectedIndex as BackupFrequency;
    // The hour is meaningless for hourly and manual-only; the day only for weekly.
    hourField.visible = f === BackupFrequency.Daily || f === BackupFrequency.Weekly;
    dayField.visible = f === BackupFrequency.Weekly;
    relayout(shell);
  }
  frequencyPicker.on('selectionchanged', showSchedule);

  function showUndeliverable(): void {
    undeliverable.visible = notifyBox.text.trim().length > 0 && !notificationsDeliverable;
    relayout(shell);
  }
  notifyBox.on('textchanged', showUndeliverable);

  // ---- Archives ----
  // Their own status lines: with fourteen archives listed, the card's line is a long way above the row
  // that was pressed and the restore panel below the list, and a refusal nobody sees reads as a button
  // that did nothing.
  const archiveStatus = new StatusLine();
  const restoreStatus = new StatusLine();
  const archiveEmpty = muted('No archives in the destination yet.');
  const archives = new CardList<BackupDto>({ key: b => b.fileName, build: b => buildArchiveRow(b), spacing: GAP.xs });

  // ---- Restore panel ----
  let restoring: RestoreSource | null = null;
  const restoreTitle = text('', { font: FONT.bold });
  const confirmBox = textBox({ placeholder: BackupLimits.RestorePhrase });
  const restorePanel = card({ tint: 'danger', visible: false });
  restorePanel.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm }),
      restoreTitle,
      text(
        `This replaces ${organization}'s ENTIRE database with the contents of the chosen archive - every customer, device, ticket and invoice recorded since it was taken is lost. The database being replaced is kept beside it as jetsoncrm.db.replaced. No other organization is touched.`
      ),
      text(
        `Restore and restart applies it at once, signing ${organization}'s people out for a few seconds; Stage only waits for its next restart. Type ${BackupLimits.RestorePhrase} below to confirm.`
      ),
      field('Confirmation', confirmBox, { width: 240 }),
      stackOf(
        buttonRow(),
        button('Restore and restart', () => void stageRestore(true), true),
        button('Stage only', () => void stageRestore(false)),
        button('Cancel', () => closeRestore())
      ),
      restoreStatus.label
    )
  );

  // ---- Run log ----
  const runEmpty = muted('No backup has run yet.');
  const runs = new CardList<BackupRunDto>({
    key: r => `${r.startedUtc}|${r.fileName ?? ''}`,
    build: r => buildRunRow(r),
    spacing: GAP.xs
  });

  const archiveTools = canEdit ? [stackOf(buttonRow(), button('Restore from a file…', () => void pickArchive()))] : [];

  const shell = card();
  shell.controls.add(
    stackOf(
      vstack({ spacing: GAP.md }),
      h2(organization),
      muted(
        `Scheduled full backups of ${organization}, written to a folder of its own. Each archive holds its database, its knowledge-base, forum and installer files, and the encryption keys every stored password depends on - so a restore comes back complete rather than with unreadable credentials. An archive names the organization it came from and can only be restored into it.`
      ),
      stagedBanner,
      lastRunLine,
      nextRunLine,
      destinationError,

      text('Destination', { font: FONT.bold }),
      field('Folder', pathBox),
      muted(
        "A folder no other organization uses - archives are named by date alone, so two organizations in one folder would list and prune each other's. On the Synology itself this is a path inside a shared folder, such as /volume1/backups/jetsoncrm/northwind - make sure the DSM \"package\" user may write to it. From another machine, use a network path such as \\\\diskstation\\backups\\northwind and give a sign-in below."
      ),
      fieldRow(field('Sign-in name (network path only)', userBox), field('Password', passwordInput)),
      passwordState,
      checkLine(clearPassword),

      text('Schedule', { font: FONT.bold }),
      checkLine(enabledCheck),
      field('How often', frequencyPicker),
      fieldRow(dayField, hourField),

      text('Keep', { font: FONT.bold }),
      fieldRow(field('Most recent archives', keepCountBox), field('Discard after (days)', keepDaysBox)),
      muted(
        '0 means no limit. Old archives are only removed after a backup succeeds, so a run of failures can never leave you with nothing, and the newest archive is never discarded on age alone.'
      ),

      text('Tell me when it breaks', { font: FONT.bold }),
      field('Email address', notifyBox),
      muted(
        "Emailed through the host's mail account when a scheduled backup fails, again once a day while it keeps failing, and once more when it starts working again. Separate several addresses with commas; leave blank for no emails. A backup you start yourself never sends one - you are already looking at the result."
      ),
      undeliverable,

      checkLine(includeMedia),
      muted(
        'Leave this on unless those files are backed up separately - without them a restore brings back every article and forum post with its screenshots broken, and every uploaded installer missing. The encryption keys travel either way.'
      ),
      stackOf(buttonRow(), ...actionButtons),
      testVerdict,
      status.label,

      text('Archives in the destination', { font: FONT.bold }),
      muted(
        'Read from the folder itself, so an archive copied in by hand is listed too. Download saves a copy through this browser; Restore from a file takes an archive from this computer, for when the folder itself is gone.'
      ),
      ...archiveTools,
      freeSpace,
      archiveEmpty,
      archives.panel,
      archiveStatus.label,
      restorePanel,

      text('Recent runs', { font: FONT.bold }),
      runEmpty,
      runs.panel
    )
  );

  function buildArchiveRow(b: BackupDto): Card {
    const details = stackOf(
      vstack({ spacing: GAP.xs }),
      value(b.fileName, { font: FONT.bold }),
      muted(join([bytes(b.sizeBytes), localDateTime(b.createdUtc), b.trigger === BackupTrigger.Manual ? 'Manual' : 'Scheduled']))
    );
    const row = stackOf(vstack({ spacing: GAP.sm }), details);
    if (canEdit) {
      row.controls.add(
        stackOf(
          buttonRow(),
          button('Download', () => void downloadArchive(b)),
          button('Restore', () => openRestore({ kind: 'archive', backup: b })),
          button('Delete', () => void remove(b))
        )
      );
    }
    const shell = card({ tint: 'alt' });
    shell.controls.add(row);
    return shell;
  }

  function buildRunRow(r: BackupRunDto): Card {
    const who =
      r.trigger === BackupTrigger.Manual ? `Manual${r.actor?.trim() ? ' · ' + r.actor : ''}` : 'Scheduled';
    const detail = r.success ? join([who, bytes(r.sizeBytes), r.fileName]) : join([who, r.error]);
    const heading = splitRow({ align: 'center', spacing: GAP.sm });
    heading.controls.add(chip(r.success ? 'Succeeded' : 'Failed', r.success ? 'ok' : 'danger'));
    fillWith(heading, value(localDateTime(r.startedUtc), { font: FONT.small, role: 'muted' }));
    const shell = card({ tint: 'alt' });
    shell.controls.add(stackOf(vstack({ spacing: GAP.xs }), heading, muted(detail, r.success ? 'muted' : 'danger')));
    return shell;
  }

  function bind(s: BackupStatusDto): void {
    const c = s.config;
    enabledCheck.checked = c.enabled;
    frequencyPicker.selectedIndex = c.frequency;
    hourPicker.selectedIndex = c.hourLocal >= 0 && c.hourLocal <= 23 ? c.hourLocal : 2;
    dayPicker.selectedIndex = c.dayOfWeek;
    pathBox.text = c.destinationPath ?? '';
    userBox.text = c.username ?? '';
    keepCountBox.text = String(c.keepCount);
    keepDaysBox.text = String(c.keepDays);
    includeMedia.checked = c.includeMedia;
    notifyBox.text = c.notifyEmail ?? '';
    notificationsDeliverable = s.notificationsDeliverable;

    passwordInput.text = '';
    clearPassword.checked = false;
    passwordState.text = c.hasPassword
      ? 'A password is stored. Leave the box empty to keep it.'
      : 'No password stored. Leave the box empty for a local folder on the NAS.';

    if (!c.lastRunUtc) {
      lastRunLine.text = 'No backup has been taken yet.';
      lastRunLine.role = 'muted';
    } else if (c.lastRunSucceeded) {
      lastRunLine.text = `Last backup succeeded ${localDateTime(c.lastRunUtc)}.`;
      lastRunLine.role = 'ok';
    } else {
      lastRunLine.text = `Last backup FAILED at ${localDateTime(c.lastRunUtc)}: ${c.lastRunError ?? ''}`;
      lastRunLine.role = 'danger';
    }
    nextRunLine.text = !c.runsOnSchedule
      ? 'Scheduled backups are off - they only run when you ask for one.'
      : c.nextDueUtc
        ? `Next scheduled backup: ${localDateTime(c.nextDueUtc)}.`
        : '';
    nextRunLine.visible = nextRunLine.text.length > 0;

    destinationError.text = s.destinationError ?? '';
    destinationError.visible = destinationError.text.length > 0;
    freeSpace.text = s.destinationFreeBytes != null ? `${bytes(s.destinationFreeBytes)} free on the destination.` : '';
    freeSpace.visible = freeSpace.text.length > 0;

    stagedBanner.visible = s.restorePending;
    archives.set(s.backups ?? []);
    archiveEmpty.visible = (s.backups ?? []).length === 0;
    runs.set(s.recentRuns ?? []);
    runEmpty.visible = (s.recentRuns ?? []).length === 0;

    showSchedule();
    showUndeliverable();
  }

  function buildRequest(): UpdateBackupConfigRequest | string {
    const keepCount = whole(keepCountBox.text);
    if (keepCount === null) return 'Keep must be a whole number of archives (0 for no limit).';
    const keepDays = whole(keepDaysBox.text);
    if (keepDays === null) return 'The age limit must be a whole number of days (0 for no limit).';
    const frequency = frequencyPicker.selectedIndex as BackupFrequency;
    if (enabledCheck.checked && frequency !== BackupFrequency.Manual && !pathBox.text.trim())
      return 'Enter a destination folder before switching scheduled backups on.';

    return {
      enabled: enabledCheck.checked,
      frequency,
      hourLocal: hourPicker.selectedIndex,
      dayOfWeek: dayPicker.selectedIndex as DayOfWeek,
      destinationPath: pathBox.text.trim(),
      username: userBox.text.trim(),
      password: clearPassword.checked ? '' : passwordInput.text.length > 0 ? passwordInput.text : null,
      keepCount,
      keepDays,
      includeMedia: includeMedia.checked,
      notifyEmail: notifyBox.text.trim()
    };
  }

  function setBusy(busy: boolean): void {
    for (const b of actionButtons) b.enabled = canEdit && !busy;
  }

  async function load(): Promise<void> {
    try {
      const s = await api.status(ctx.dispose.signal());
      if (!ctx.cancelled()) bind(s);
    } catch (error) {
      if (!ctx.cancelled()) status.error('Could not load the backup settings: ' + describeError(error));
    }
  }

  async function save(): Promise<void> {
    const req = buildRequest();
    if (typeof req === 'string') {
      status.error(req);
      return;
    }
    setBusy(true);
    status.busy('Saving…');
    try {
      await api.save(req);
      if (ctx.cancelled()) return;
      await load();
      changed();
      status.ok('Backup settings saved.');
    } catch (error) {
      if (!ctx.cancelled()) status.error(describeError(error));
    } finally {
      setBusy(false);
    }
  }

  async function test(): Promise<void> {
    const req = buildRequest();
    if (typeof req === 'string') {
      status.error(req);
      return;
    }
    setBusy(true);
    status.busy('Testing…');
    testVerdict.text = '';
    const result = await api.test(req);
    setBusy(false);
    if (ctx.cancelled()) return;
    // The probe's own line, kept apart from the status so it survives the next message.
    testVerdict.text = result.detail ?? '';
    testVerdict.visible = testVerdict.text.length > 0;
    if (result.success) status.ok(result.summary);
    else status.error(result.summary);
    relayout(shell);
  }

  async function run(): Promise<void> {
    setBusy(true);
    status.busy('Taking a backup… this can take a while on a large database.');
    const result = await api.run();
    if (ctx.cancelled()) return;
    // Reload either way: a failure is now in the run log, which is where an operator looks next.
    await load();
    changed();
    setBusy(false);
    if (ctx.cancelled()) return;
    if (result.success) status.ok(result.summary);
    else status.error(result.error ?? result.summary);
  }

  async function downloadArchive(b: BackupDto): Promise<void> {
    archiveStatus.busy(`Downloading ${b.fileName} - a large archive can take a moment to start.`);
    try {
      await download(api.downloadPath(b.fileName), b.fileName);
      if (!ctx.cancelled()) archiveStatus.ok(`Downloaded ${b.fileName}.`);
    } catch (error) {
      if (!ctx.cancelled()) archiveStatus.error('Could not download that backup: ' + describeError(error));
    }
  }

  async function remove(b: BackupDto): Promise<void> {
    const yes = await confirm(ctx.desktop, {
      title: 'Delete backup',
      message: `Delete ${b.fileName}?\n\nThis removes the archive from ${organization}'s backup folder. It cannot be undone.`
    });
    if (!yes || ctx.cancelled()) return;
    try {
      await api.remove(b.fileName);
      if (ctx.cancelled()) return;
      if (restoring?.kind === 'archive' && restoring.backup.fileName === b.fileName) closeRestore();
      await load();
      archiveStatus.ok(`${b.fileName} deleted.`);
    } catch (error) {
      if (!ctx.cancelled()) archiveStatus.error('Could not remove that backup: ' + describeError(error));
    }
  }

  async function pickArchive(): Promise<void> {
    const [file] = await pickFiles(false, '.zip');
    if (!file || ctx.cancelled()) return;
    openRestore({ kind: 'file', file });
  }

  function openRestore(source: RestoreSource): void {
    restoring = source;
    restoreTitle.text =
      source.kind === 'archive' ? `Restore ${source.backup.fileName}` : `Restore ${source.file.name} from this computer`;
    confirmBox.text = '';
    restorePanel.visible = true;
    restoreStatus.clear();
    archiveStatus.clear();
    relayout(shell);
  }

  function closeRestore(): void {
    restoring = null;
    confirmBox.text = '';
    restorePanel.visible = false;
    relayout(shell);
  }

  async function stageRestore(restartNow: boolean): Promise<void> {
    const source = restoring;
    if (!source) return;
    const phrase = confirmBox.text.trim();
    if (phrase.toUpperCase() !== BackupLimits.RestorePhrase) {
      restoreStatus.error(`Type ${BackupLimits.RestorePhrase} to confirm.`);
      return;
    }
    restoreStatus.busy(restartNow ? `Restoring ${organization} and restarting it…` : 'Staging the restore…');
    const result =
      source.kind === 'archive'
        ? await api.restore(source.backup.fileName, phrase, restartNow)
        : await api.restoreUpload(source.file, phrase, restartNow, f => restoreStatus.busy(`Uploading… ${Math.round(f * 100)}%`));
    if (ctx.cancelled()) return;
    if (!result.success) {
      restoreStatus.error(result.error ?? result.summary);
      return;
    }
    closeRestore();
    await load();
    if (ctx.cancelled()) return;
    changed();
    status.ok(result.summary);
    // A dialog as well as the status line: what happened reaches everyone signed in to the organization.
    await tell(ctx.desktop, result.restartRequired ? 'Restore staged' : 'Restored', result.summary);
  }

  async function applyStaged(): Promise<void> {
    const yes = await confirm(ctx.desktop, {
      title: 'Apply the restore',
      message: `Restart ${organization} now?\n\nIts database is replaced with the staged archive, and everything recorded since that backup was taken is lost. Its people are signed out for the few seconds the restart takes.`
    });
    if (!yes || ctx.cancelled()) return;
    status.busy(`Restarting ${organization}…`);
    try {
      await api.applyRestore();
      if (ctx.cancelled()) return;
      await load();
      changed();
      if (!ctx.cancelled()) status.ok(`${organization} restarted and the restore was applied.`);
    } catch (error) {
      if (!ctx.cancelled()) status.error(`Could not restart ${organization}: ` + describeError(error));
    }
  }

  async function cancelStaged(): Promise<void> {
    try {
      await api.cancelRestore();
      if (ctx.cancelled()) return;
      stagedBanner.visible = false;
      changed();
      status.ok('The staged restore was cancelled. Restarting will no longer replace the database.');
      relayout(shell);
    } catch (error) {
      if (!ctx.cancelled()) status.error('Could not cancel the staged restore: ' + describeError(error));
    }
  }

  undeliverable.visible = false;
  destinationError.visible = false;
  testVerdict.visible = false;

  return { card: shell, load };
}
